import React, { useState, useEffect, useRef, useMemo } from "react";
import { EditionOption } from "../services/analyticsService";

interface EditionMultiSelectProps {
  options: EditionOption[];
  selectedValues: number[];
  onChange: (values: number[]) => void;
  loading?: boolean;
}

const EditionMultiSelect: React.FC<EditionMultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const isAllSelected = selectedValues.length === options.length && options.length > 0;
  const hasSelection = selectedValues.length > 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = (id: number) => {
    if (selectedValues.includes(id)) {
      onChange(selectedValues.filter((v) => v !== id));
    } else {
      onChange([...selectedValues, id]);
    }
  };

  const handleSelectAll = () => {
    onChange(options.map((o) => o.id));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const filteredOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          option.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [options, searchTerm]
  );

  const getButtonText = () => {
    if (loading) return "Loading...";
    if (selectedValues.length === 0) return "All Editions";
    if (selectedValues.length === 1) {
      return options.find((o) => o.id === selectedValues[0])!.name;
    }
    if (isAllSelected) return "All Editions";
    return `${selectedValues.length} editions`;
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        style={{
          padding: "8px 12px",
          border: "1px solid #e2e8f0",
          borderRadius: "4px",
          backgroundColor: "#fff",
          cursor: loading ? "not-allowed" : "pointer",
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          minWidth: "180px",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {getButtonText()}
        </span>
        <span style={{ fontSize: "10px" }}>{isOpen ? "\u25B2" : "\u25BC"}</span>
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            minWidth: "300px",
            maxWidth: "400px",
            backgroundColor: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
            maxHeight: "350px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ padding: "8px", borderBottom: "1px solid #e2e8f0" }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search editions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "6px 10px",
                border: "1px solid #e2e8f0",
                borderRadius: "4px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div
            style={{
              padding: "4px 8px",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <button
              type="button"
              onClick={hasSelection ? handleClearAll : handleSelectAll}
              style={{
                background: "none",
                border: "none",
                color: "#4A5568",
                cursor: "pointer",
                fontSize: "12px",
                padding: "4px 8px",
              }}
            >
              {hasSelection ? "Clear all" : "Select all"}
            </button>
            <span style={{ fontSize: "12px", color: "#718096" }}>
              {selectedValues.length} of {options.length} selected
            </span>
          </div>

          <div style={{ overflowY: "auto", padding: "8px" }}>
            {filteredOptions.length === 0 ? (
              <div
                style={{ padding: "12px", textAlign: "center", color: "#718096" }}
              >
                No editions found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.id);
                return (
                  <label
                    key={option.id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      padding: "8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#EBF4FF" : "transparent",
                      gap: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggle(option.id)}
                      style={{ marginTop: "2px" }}
                    />
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#2D3748",
                      }}
                    >
                      {option.name}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditionMultiSelect;
